import React, { useEffect, useState } from "react";
import {
  getApplication,
  submitApplication,
} from "../../../services/user.services";
import { useSelector } from "react-redux";
import Loader from "../../admin/leads/Loader";
import Select from "react-select";
import currencies from "world-currencies";
import toast, { Toaster } from "react-hot-toast";
import { formatDateTime } from "../../../utils/dateFormat";

// Format currency options
const CURRENCY_OPTIONS = Object.entries(currencies)
  .map(([code, currency]) => ({
    value: currency.iso.code,
    label: `${currency.name} (${currency.iso.code})`,
    symbol: currency.units?.major?.symbol || "N/A",
  }))
  .filter((currency) => currency.symbol);

// Better field display names with proper capitalization
const fieldDisplayNames = {
  intended_courses: "Intended Courses",
  scores: "Test Scores",
  comments: "Other Info",
  country_preference: "Preferred Country",
  university_choices: "University Choices",
  budget: "Budget",
  currency: "Currency",
  intakeMonth: "Intake Month",
  intakeYear: "Intake Year",
};

const getDisplayName = (key) =>
  fieldDisplayNames[key] ||
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Month and year options for intake selection
const monthOptions = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
].map((month) => ({ value: month, label: month }));

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 11 }, (_, index) => {
  const year = currentYear + index;
  return { value: year.toString(), label: year.toString() };
});

const currentDate = new Date();
const currentMonth = currentDate.getMonth(); // 0-11

const generateMonthOptions = (selectedYear) => {
  if (selectedYear && parseInt(selectedYear) === currentYear) {
    return monthOptions.filter((_, index) => index >= currentMonth);
  }
  return monthOptions;
};

const ShortListingUniversityForm = () => {
  const userId = useSelector((state) => state.user.user_id);

  // State management
  const [formData, setFormData] = useState({
    intended_courses: "",
    intakeMonth: null,
    intakeYear: null,
    scores: "",
    country_preference: "",
    budget: "",
    currency: "",
    university_choices: "",
    comments: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [availableMonths, setAvailableMonths] = useState(
    generateMonthOptions(formData.intakeYear)
  );
  // Fields that should be excluded from rendering directly
  const excludedFields = ["created_at", "student_id", "id", "intake"];

  // Optional fields that don't require validation
  const optionalFields = [
    "comments",
    "scores",
    "country_preference",
    "university_choices",
  ];

  // Fetch existing application data
  useEffect(() => {
    const loadData = async () => {
      try {
        const res = await getApplication(userId);
        if (res && Object.values(res).some((value) => value !== "")) {
          // Parse intake date back into month and year if it exists
          if (res.intake) {
            const intakeDate = new Date(res.intake);
            const month = monthOptions.find(
              (m) =>
                m.value ===
                intakeDate.toLocaleString("en-US", { month: "long" })
            )?.value;
            const year = intakeDate.getFullYear().toString();

            res.intakeMonth = month || res.intakeMonth;
            res.intakeYear = year || res.intakeYear;
          }

          setFormData(res);
          setIsSubmitted(true); // Mark form as submitted if data exists
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };
    loadData();
  }, [userId]);

  // Handle intake date updates
  const updateIntake = (month, year) => {
    if (month && year) {
      const monthIndex = {
        January: 0,
        February: 1,
        March: 2,
        April: 3,
        May: 4,
        June: 5,
        July: 6,
        August: 7,
        September: 8,
        October: 9,
        November: 10,
        December: 11,
      };

      // Create a date object in UTC
      const date = new Date(Date.UTC(year, monthIndex[month], 1));
      const formattedDate = date.toISOString().split(".")[0] + "Z";

      setFormData((prevState) => ({
        ...prevState,
        intakeMonth: month,
        intakeYear: year,
        intake: formattedDate,
      }));
    }
  };

  // Handle month selection change
  const handleMonthChange = (selectedOption) => {
    const month = selectedOption ? selectedOption.value : "";
    const monthIndex = monthOptions.findIndex((m) => m.value === month);

    // If year is selected and it's current year, reject past month
    if (
      formData.intakeYear &&
      parseInt(formData.intakeYear) === currentYear &&
      monthIndex < currentMonth
    ) {
      setFormData((prev) => ({ ...prev, intakeMonth: "" }));
      return;
    }

    setFormData((prevState) => ({
      ...prevState,
      intakeMonth: month,
    }));
    updateIntake(month, formData.intakeYear);
    setErrors({ ...errors, intakeMonth: "" });
  };

  // Handle year selection change
  const handleYearChange = (selectedOption) => {
    const year = selectedOption ? selectedOption.value : "";
    setAvailableMonths(generateMonthOptions(year));

    const selectedMonthIndex = monthOptions.findIndex(
      (m) => m.value === formData.intakeMonth
    );

    if (year && parseInt(year) === currentYear) {
      if (selectedMonthIndex < currentMonth) {
        const fallbackMonth = generateMonthOptions(year)[0]?.value || "";
        setFormData((prevState) => ({
          ...prevState,
          intakeYear: year,
          intakeMonth: fallbackMonth,
        }));
        updateIntake(fallbackMonth, year);
        return;
      }
    }

    setFormData((prevState) => ({
      ...prevState,
      intakeYear: year,
    }));
    updateIntake(formData.intakeMonth, year);
    setErrors({ ...errors, intakeYear: "" });
  };

  // Handle form field changes
  const handleChange = (e, field) => {
    const { value } = e.target;
    setFormData({ ...formData, [field]: value });
    setErrors({ ...errors, [field]: "" });
  };

  // Validate form before submission
  const validateForm = () => {
    let newErrors = {};

    // Check required fields
    Object.keys(formData).forEach((key) => {
      if (
        !optionalFields.includes(key) &&
        !excludedFields.includes(key) &&
        !formData[key]
      ) {
        newErrors[key] = "This field is required";
      }
    });

    // Special validation for intake
    if (!formData.intakeMonth) {
      newErrors.intakeMonth = "Please select an intake month";
    }
    if (!formData.intakeYear) {
      newErrors.intakeYear = "Please select an intake year";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitted) {
      toast.error("Form already submitted. You cannot submit again.");
      return;
    }

    if (!validateForm()) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setIsSubmitting(true);
    try {
      await submitApplication(formData);
      setIsSubmitted(true);
      toast.success("Form submitted successfully!");
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Submission failed, please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <Toaster position="top-right" />

      <form
        className="space-y-6 bg-white p-6 rounded-lg shadow-sm"
        onSubmit={handleSubmit}
      >
        {/* Success message */}
        {isSubmitted && (
          <div className="p-4 border border-green-200 rounded-md bg-green-50">
            <h2 className="text-green-700 font-semibold text-lg flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              Form Submitted Successfully
            </h2>
            <p className="text-gray-700 mt-1">
              The data below has been submitted and cannot be edited.
            </p>
          </div>
        )}

        {/* Form section - Intended Courses */}
        <div className="grid md:grid-cols-3 gap-4 items-start">
          <label className="font-medium text-gray-700">
            Intended Courses <span className="text-red-500">*</span>
          </label>
          <div className="md:col-span-2">
            <input
              type="text"
              value={formData.intended_courses || ""}
              onChange={(e) => handleChange(e, "intended_courses")}
              className={`w-full p-2.5 rounded-md border ${
                errors.intended_courses ? "border-red-500" : "border-gray-300"
              } focus:outline-none focus:ring-2 focus:ring-indigo-500`}
              placeholder="E.g., Computer Science, Business Administration"
              disabled={isSubmitted}
            />
            {errors.intended_courses && (
              <p className="mt-1 text-sm text-red-600">
                {errors.intended_courses}
              </p>
            )}
          </div>
        </div>

        {/* Form section - Intake */}
        <div className="grid md:grid-cols-3 gap-4 items-start">
          <label className="font-medium text-gray-700">
            Intake <span className="text-red-500">*</span>
          </label>
          <div className="md:col-span-2 grid grid-cols-2 gap-4">
            <div>
              <Select
                options={availableMonths}
                value={monthOptions.find(
                  (option) => option.value === formData.intakeMonth
                )}
                onChange={handleMonthChange}
                isDisabled={isSubmitted}
                placeholder="Select Month"
                className={errors.intakeMonth ? "border-red-500" : ""}
              />
              {errors.intakeMonth && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.intakeMonth}
                </p>
              )}
            </div>
            <div>
              <Select
                options={yearOptions}
                value={yearOptions.find(
                  (option) => option.value === formData.intakeYear
                )}
                onChange={handleYearChange}
                isDisabled={isSubmitted}
                placeholder="Select Year"
                className={errors.intakeYear ? "border-red-500" : ""}
              />
              {errors.intakeYear && (
                <p className="mt-1 text-sm text-red-600">{errors.intakeYear}</p>
              )}
            </div>
          </div>
        </div>

        {/* Form section - Test Scores */}
        <div className="grid md:grid-cols-3 gap-4 items-start">
          <label className="font-medium text-gray-700">Test Scores</label>
          <div className="md:col-span-2">
            <input
              type="text"
              value={formData.scores || ""}
              onChange={(e) => handleChange(e, "scores")}
              className="w-full p-2.5 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="E.g., IELTS 7.5, GRE 320"
              disabled={isSubmitted}
            />
          </div>
        </div>

        {/* Form section - Preferred Country */}
        <div className="grid md:grid-cols-3 gap-4 items-start">
          <label className="font-medium text-gray-700">Preferred Country</label>
          <div className="md:col-span-2">
            <input
              type="text"
              value={formData.country_preference || ""}
              onChange={(e) => handleChange(e, "country_preference")}
              className="w-full p-2.5 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="E.g., USA, UK, Canada"
              disabled={isSubmitted}
            />
          </div>
        </div>

        {/* Form section - Budget */}
        <div className="grid md:grid-cols-3 gap-4 items-start">
          <label className="font-medium text-gray-700">
            Budget <span className="text-red-500">*</span>
          </label>
          <div className="md:col-span-2 flex gap-3">
            <div className="w-1/3">
              <Select
                options={CURRENCY_OPTIONS}
                value={CURRENCY_OPTIONS.find(
                  (opt) => opt.value === formData.currency
                )}
                onChange={(option) =>
                  setFormData({
                    ...formData,
                    currency: option ? option.value : "",
                  })
                }
                isDisabled={isSubmitted}
                placeholder="Currency"
                className={errors.currency ? "border-red-500" : ""}
              />
              {errors.currency && (
                <p className="mt-1 text-sm text-red-600">{errors.currency}</p>
              )}
            </div>
            <div className="flex-1">
              <input
                type="number"
                value={formData.budget || ""}
                onChange={(e) => handleChange(e, "budget")}
                className={`w-full p-2.5 rounded-md border ${
                  errors.budget ? "border-red-500" : "border-gray-300"
                } focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                placeholder="Enter amount"
                disabled={isSubmitted}
              />
              {errors.budget && (
                <p className="mt-1 text-sm text-red-600">{errors.budget}</p>
              )}
            </div>
          </div>
        </div>

        {/* Form section - University Choices */}
        <div className="grid md:grid-cols-3 gap-4 items-start">
          <label className="font-medium text-gray-700">
            University Choices
          </label>
          <div className="md:col-span-2">
            <input
              type="text"
              value={formData.university_choices || ""}
              onChange={(e) => handleChange(e, "university_choices")}
              className="w-full p-2.5 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="E.g., Stanford, MIT, Oxford"
              disabled={isSubmitted}
            />
          </div>
        </div>

        {/* Form section - Additional Comments */}
        <div className="grid md:grid-cols-3 gap-4 items-start">
          <label className="font-medium text-gray-700">
            Additional Comments
          </label>
          <div className="md:col-span-2">
            <textarea
              value={formData.comments || ""}
              onChange={(e) => handleChange(e, "comments")}
              className="w-full p-2.5 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              rows={4}
              placeholder="Any additional information you'd like to share"
              disabled={isSubmitted}
            />
          </div>
        </div>

        {/* Form actions */}
        <div className="flex justify-end space-x-4 pt-4 border-t">
          <button
            type="button"
            className="px-4 py-2 text-gray-700 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50 transition"
            onClick={() => window.history.back()}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || isSubmitted}
            className={`px-6 py-2 rounded-md text-white font-medium ${
              isSubmitted
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-indigo-600 hover:bg-indigo-700 transition"
            }`}
          >
            {isSubmitting ? <Loader /> : isSubmitted ? "Submitted" : "Submit"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ShortListingUniversityForm;
